import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useStory } from '../../context/StoryContext';

const tones = ['Dramatic', 'Whimsical', 'Gritty', 'Romantic', 'Suspenseful', 'Epic', 'Melancholic', 'Humorous'];
const vocabularyLevels = ['Simple', 'Conversational', 'Literary', 'Archaic', 'Technical'];
const responseLengths = ['Brief', 'Medium', 'Long', 'Epic'];
const focusOptions = ['Character-Centric', 'World-Centric', 'Plot-Driven', 'Dialogue-Heavy'];

const SliderRow = ({ label, value, min, max, step, onChange, displayFn }) => (
  <div className="space-y-2">
    <div className="flex justify-between items-center">
      <span className="text-[10px] font-black uppercase tracking-widest text-gray-900 dark:text-purple-400">{label}</span>
      <span className="text-[11px] font-black text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
        {displayFn ? displayFn(value) : value}
      </span>
    </div>
    <input
      type="range" min={min} max={max} step={step} value={value}
      onChange={e => onChange(parseFloat(e.target.value))}
      className="w-full"
    />
  </div>
);

const ChipSelector = ({ label, options, value, onChange }) => (
  <div className="space-y-2">
    <span className="text-[10px] font-black uppercase tracking-widest text-gray-900 dark:text-purple-400">{label}</span>
    <div className="flex flex-wrap gap-1.5">
      {options.map(opt => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          className={`px-3 py-1 rounded-full text-[10px] font-bold border transition-all ${
            value === opt
              ? 'bg-amber-500 border-amber-500 text-white shadow-md shadow-amber-500/20'
              : 'bg-white dark:bg-purple-900/40 border-gray-200 dark:border-purple-700 text-gray-900 dark:text-purple-300 hover:border-amber-400 hover:text-amber-600'
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

const ToggleRow = ({ label, desc, value, onChange }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-purple-50 dark:border-purple-800/60 last:border-0">
    <div>
      <p className="text-[11px] font-bold text-gray-900 dark:text-purple-200">{label}</p>
      <p className="text-[9px] text-gray-900 dark:text-purple-400 mt-0.5">{desc}</p>
    </div>
    <button
      onClick={() => onChange(!value)}
      className={`relative w-10 h-5 rounded-full transition-all duration-300 ${value ? 'bg-amber-500' : 'bg-purple-200 dark:bg-purple-700'}`}
    >
      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-300 ${value ? 'translate-x-5' : 'translate-x-0.5'}`} />
    </button>
  </div>
);

const PresetCard = ({ name, icon, desc, settings, onApply }) => (
  <button
    onClick={() => onApply(settings)}
    className="w-full p-3 rounded-xl border border-gray-100 dark:border-purple-800 bg-white dark:bg-purple-900/40 text-left hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-all group"
  >
    <div className="flex items-center space-x-2 mb-1">
      <span className="text-base">{icon}</span>
      <span className="text-[11px] font-black text-gray-900 dark:text-white group-hover:text-amber-700 dark:group-hover:text-amber-400 transition-colors">{name}</span>
    </div>
    <p className="text-[9px] text-gray-900 dark:text-purple-400 leading-relaxed">{desc}</p>
  </button>
);

const PRESETS = [
  {
    name: 'Epic Fantasy', icon: '⚔️',
    desc: 'Grand, literary prose with deep world description and dramatic tension.',
    settings: { tone: 'Epic', vocabulary: 'Literary', responseLength: 'Long', creativity: 0.8, descriptionDepth: 0.9, showEmotions: true, useDialogue: true, focus: 'World-Centric' }
  },
  {
    name: 'Pulp Thriller', icon: '🔫',
    desc: 'Short punchy sentences, tense atmosphere, dialogue-driven action.',
    settings: { tone: 'Suspenseful', vocabulary: 'Conversational', responseLength: 'Brief', creativity: 0.5, descriptionDepth: 0.3, showEmotions: false, useDialogue: true, focus: 'Plot-Driven' }
  },
  {
    name: 'Dark Romance', icon: '🌹',
    desc: 'Emotionally rich, melancholic with strong character interiority.',
    settings: { tone: 'Romantic', vocabulary: 'Literary', responseLength: 'Medium', creativity: 0.75, descriptionDepth: 0.7, showEmotions: true, useDialogue: true, focus: 'Character-Centric' }
  },
  {
    name: 'Cosmic Horror', icon: '🌀',
    desc: 'Archaic language, dread-filled, sparse dialogue, atmospheric depth.',
    settings: { tone: 'Melancholic', vocabulary: 'Archaic', responseLength: 'Long', creativity: 0.9, descriptionDepth: 0.85, showEmotions: false, useDialogue: false, focus: 'World-Centric' }
  },
];

const AICustomization = () => {
  const { state, updateAISettings } = useStory();
  const s = state.ai.settings;
  const [activeSection, setActiveSection] = useState('style');

  const sections = [
    { id: 'style', label: 'Style' },
    { id: 'depth', label: 'Depth' },
    { id: 'presets', label: 'Presets' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h4 className="text-[10px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest flex items-center space-x-2">
          <SafeIcon icon={FiIcons.FiSliders} className="text-amber-500" />
          <span>AI Response Tuning</span>
        </h4>
        <div className="flex bg-gray-100 dark:bg-purple-900/40 rounded-lg p-0.5 border border-gray-200 dark:border-purple-800">
          {sections.map(sec => (
            <button
              key={sec.id}
              onClick={() => setActiveSection(sec.id)}
              className={`px-2.5 py-1 text-[9px] font-black uppercase rounded-md transition-all ${
                activeSection === sec.id
                  ? 'bg-white dark:bg-purple-800 text-amber-600 shadow-sm'
                  : 'text-gray-900 dark:text-purple-400 hover:text-gray-900 dark:hover:text-gray-800'
              }`}
            >
              {sec.label}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {activeSection === 'style' && (
          <motion.div key="style" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="space-y-5">
            <ChipSelector
              label="Narrative Tone"
              options={tones}
              value={s.tone}
              onChange={v => updateAISettings({ tone: v })}
            />
            <ChipSelector
              label="Vocabulary Level"
              options={vocabularyLevels}
              value={s.vocabulary}
              onChange={v => updateAISettings({ vocabulary: v })}
            />
            <ChipSelector
              label="Response Length"
              options={responseLengths}
              value={s.responseLength}
              onChange={v => updateAISettings({ responseLength: v })}
            />
            <ChipSelector
              label="Narrative Focus"
              options={focusOptions}
              value={s.focus}
              onChange={v => updateAISettings({ focus: v })}
            />
          </motion.div>
        )}

        {activeSection === 'depth' && (
          <motion.div key="depth" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="space-y-6">
            <SliderRow
              label="Creativity"
              value={s.creativity}
              min={0.1} max={1.0} step={0.05}
              onChange={v => updateAISettings({ creativity: v })}
              displayFn={v => v.toFixed(2)}
            />
            <SliderRow
              label="Description Depth"
              value={s.descriptionDepth}
              min={0.1} max={1.0} step={0.05}
              onChange={v => updateAISettings({ descriptionDepth: v })}
              displayFn={v => `${Math.round(v * 100)}%`}
            />
            <div className="pt-2 space-y-1">
              <ToggleRow
                label="Show Character Emotions"
                desc="Weave internal emotional states into prose"
                value={s.showEmotions}
                onChange={v => updateAISettings({ showEmotions: v })}
              />
              <ToggleRow
                label="Include NPC Dialogue"
                desc="Generate spoken lines from supporting characters"
                value={s.useDialogue}
                onChange={v => updateAISettings({ useDialogue: v })}
              />
            </div>

            {/* Live Preview Badge */}
            <div className="p-3 bg-gray-50 dark:bg-purple-900/40 rounded-xl border border-gray-100 dark:border-purple-800">
              <p className="text-[9px] font-black text-gray-900 dark:text-purple-400 uppercase mb-2">Current Profile</p>
              <div className="flex flex-wrap gap-1.5">
                {[s.tone, s.vocabulary, s.responseLength, s.focus].map(tag => (
                  <span key={tag} className="text-[9px] px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full font-bold">{tag}</span>
                ))}
                <span className="text-[9px] px-2 py-0.5 bg-gray-100 dark:bg-purple-800 text-gray-900 dark:text-purple-300 rounded-full font-bold">
                  Creativity {s.creativity?.toFixed(1)}
                </span>
              </div>
            </div>
          </motion.div>
        )}

        {activeSection === 'presets' && (
          <motion.div key="presets" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="space-y-3">
            <p className="text-[10px] text-gray-900 dark:text-purple-400 leading-relaxed">Apply a curated preset to instantly tune all AI response settings.</p>
            {PRESETS.map(preset => (
              <PresetCard
                key={preset.name}
                {...preset}
                onApply={settings => {
                  updateAISettings(settings);
                  setActiveSection('style');
                }}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AICustomization;